import React, { useState, useMemo } from 'react';
import {
  Layers,
  Plus,
  Trash2,
  RefreshCw,
  Download,
  Settings,
  HelpCircle,
  Zap,
  Disc,
  Link,
  Shield,
  FileSpreadsheet,
  Check,
  Search,
  Filter,
  AlertCircle,
  Wrench,
  BookOpen,
  ChevronDown,
  ChevronRight,
  MapPin,
  Route,
  Info,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { useApp } from '../../context/AppContext';
import { MaterialItem, MaterialItemCategory, ItemLocationDetail } from '../../types';

export const MaterialListManager: React.FC = () => {
  const {
    bomItems,
    addBomItem,
    updateBomItem,
    removeBomItem,
    necConfig,
    setNecConfig,
    recalculateBom,
    selectedFileName,
  } = useApp();

  // Local UI State
  const [activeCategory, setActiveCategory] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [copiedToast, setCopiedToast] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  // New Custom Item Form State
  const [newItemCategory, setNewItemCategory] = useState<MaterialItemCategory>('CUSTOM');
  const [newItemDesc, setNewItemDesc] = useState('');
  const [newItemSize, setNewItemSize] = useState('');
  const [newItemSpec, setNewItemSpec] = useState('');
  const [newItemQty, setNewItemQty] = useState(1);
  const [newItemUnit, setNewItemUnit] = useState('ea');
  const [newItemLocation, setNewItemLocation] = useState('');
  const [newItemPurpose, setNewItemPurpose] = useState('');
  const [newItemNec, setNewItemNec] = useState('NEC Standard');

  // Filtered Items
  const filteredItems = useMemo(() => {
    return bomItems.filter((item) => {
      const matchCat = activeCategory === 'ALL' || item.category === activeCategory;
      const matchSearch =
        searchTerm === '' ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.tradeSize.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.specification && item.specification.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.necReference && item.necReference.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.quantityDerivation && item.quantityDerivation.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.locations &&
          item.locations.some(
            (loc) =>
              loc.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
              loc.purpose.toLowerCase().includes(searchTerm.toLowerCase())
          ));
      return matchCat && matchSearch;
    });
  }, [bomItems, activeCategory, searchTerm]);

  // Totals & KPI Metrics (Quantities only, no costs)
  const metrics = useMemo(() => {
    let wireFt = 0;
    let conduitFt = 0;
    let strutStraps = 0;
    let couplings = 0;
    let connectors = 0;
    const circuitSet = new Set<string>();

    bomItems.forEach((item) => {
      if (item.category === 'WIRE') {
        wireFt += item.quantity;
      } else if (item.category === 'CONDUIT') {
        conduitFt += item.quantity;
      } else if (item.category === 'STRUT_STRAP') {
        strutStraps += item.quantity;
      } else if (item.category === 'COUPLING') {
        couplings += item.quantity;
      } else if (item.category === 'CONNECTOR') {
        connectors += item.quantity;
      }

      if (item.locations) {
        item.locations.forEach((l) => {
          if (l.runDescription) circuitSet.add(l.runDescription);
        });
      }
    });

    return {
      wireFt,
      conduitFt,
      strutStraps,
      couplings,
      connectors,
      conduitSticks: Math.ceil(conduitFt / (necConfig.conduitStickLengthFt || 10)),
      totalCircuits: circuitSet.size || 6,
    };
  }, [bomItems, necConfig.conduitStickLengthFt]);

  // Handle Add Item Submit
  const handleAddItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemDesc.trim()) return;

    const loc: ItemLocationDetail = {
      location: newItemLocation.trim() || 'Custom Installation Location',
      quantity: Number(newItemQty) || 1,
      purpose: newItemPurpose.trim() || 'Manual material requirement',
      necCitation: newItemNec.trim() || 'NEC Standard',
    };

    addBomItem({
      category: newItemCategory,
      description: newItemDesc.trim(),
      tradeSize: newItemSize.trim() || 'Standard',
      specification: newItemSpec.trim() || 'Manual addition',
      quantity: Number(newItemQty) || 1,
      unit: newItemUnit,
      isAutoGenerated: false,
      necReference: newItemNec.trim() || 'NEC Standard',
      quantityDerivation: `Manually added: ${newItemQty} ${newItemUnit} for ${loc.location}.`,
      locations: [loc],
    });

    // Reset
    setNewItemDesc('');
    setNewItemSize('');
    setNewItemSpec('');
    setNewItemLocation('');
    setNewItemPurpose('');
    setNewItemQty(1);
    setShowAddModal(false);
  };

  // Export to Excel (.xlsx) with installation location breakdowns
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    // 1. Bill of Materials Sheet
    const bomRows = bomItems.map((item, idx) => ({
      'Item #': idx + 1,
      Category: item.category,
      Description: item.description,
      'Trade Size': item.tradeSize,
      Quantity: item.quantity,
      Unit: item.unit,
      'NEC Regulation Reference': item.necReference || 'NEC Standard',
      'Why This Quantity Was Obtained': item.quantityDerivation || 'Extracted from plan takeoff',
      'Installation Locations Summary': (item.locations || [])
        .map((l) => `${l.quantity}x at ${l.location} (${l.purpose})`)
        .join('; '),
      Specification: item.specification || '',
      Type: item.isAutoGenerated ? 'Auto-Calculated (NEC)' : 'Manual Item',
    }));

    const wsBOM = XLSX.utils.json_to_sheet(bomRows);
    wsBOM['!cols'] = [
      { wch: 8 },
      { wch: 14 },
      { wch: 38 },
      { wch: 12 },
      { wch: 10 },
      { wch: 8 },
      { wch: 30 },
      { wch: 60 },
      { wch: 70 },
      { wch: 35 },
      { wch: 20 },
    ];
    XLSX.utils.book_append_sheet(wb, wsBOM, 'Bill of Materials (BOM)');

    // 2. Specific Installation Locations Breakdown Sheet
    const locationRows: any[] = [];
    bomItems.forEach((item) => {
      (item.locations || []).forEach((loc) => {
        locationRows.push({
          Category: item.category,
          'Material Item': item.description,
          'Trade Size': item.tradeSize,
          'Quantity at Location': loc.quantity,
          Unit: item.unit,
          'Installation Location': loc.location,
          'Specific Purpose / Knockout Detail': loc.purpose,
          'Associated Circuit / Run': loc.runDescription || 'General Installation',
          'NEC Code Citation': loc.necCitation || item.necReference || 'NEC Standard',
        });
      });
    });

    const wsLocations = XLSX.utils.json_to_sheet(locationRows);
    wsLocations['!cols'] = [
      { wch: 14 },
      { wch: 35 },
      { wch: 12 },
      { wch: 18 },
      { wch: 8 },
      { wch: 35 },
      { wch: 50 },
      { wch: 35 },
      { wch: 25 },
    ];
    XLSX.utils.book_append_sheet(wb, wsLocations, 'Installation Locations');

    // 3. Summary KPI Sheet
    const summaryData = [
      ['--- HELIOS ONE ELECTRICAL MATERIAL QUANTITIES SUMMARY ---'],
      ['Project Plan:', selectedFileName || 'Commercial Solar System'],
      ['Date Exported:', new Date().toLocaleDateString()],
      [],
      ['Metric', 'Quantity', 'Unit', 'Notes'],
      ['Total Conductors & Wire Footage', Math.round(metrics.wireFt), 'ft', 'Includes phase conductors and EGC ground conductors'],
      ['Total Conduit Raceway Footage', Math.round(metrics.conduitFt), 'ft', `${metrics.conduitSticks} standard 10ft sticks`],
      ['Total Strut Straps (NEC Supports)', metrics.strutStraps, 'ea', 'NEC 358.30 / 344.30 (within 3ft of boxes & max 10ft intervals)'],
      ['Total Couplings (10ft Stick Transitions)', metrics.couplings, 'ea', 'Calculated per 10ft segment transitions'],
      ['Total Enclosure Connectors & Hubs', metrics.connectors, 'ea', 'Myers Hubs & Raintight Connectors per NEC 314.15 & 312.2'],
      ['Total Circuits / Feeder Runs', metrics.totalCircuits, 'runs', 'Distinct wire schedule raceway runs'],
      [],
      ['--- NEC REGULATORY BASIS ---'],
      ['Conduit Strapping Support Interval:', 'NEC 358.30 (EMT) / NEC 344.30 (RMC) / NEC 352.30 (PVC)'],
      ['Enclosure Connectors & Wet Locations:', 'NEC 314.15 (Wet Locations) & NEC 312.2 (Weatherproof Enclosures)'],
      ['Grounding & Bonding Bushings:', 'NEC 250.92 & 250.97 (Service Conduits & Circuits Over 250V to Ground)'],
    ];

    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    wsSummary['!cols'] = [{ wch: 35 }, { wch: 16 }, { wch: 12 }, { wch: 45 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary Quantities');

    const fileName = `${(selectedFileName || 'Solar_Takeoff').replace(/\.[^/.]+$/, '')}_Material_BOM.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Copy BOM as TSV to Clipboard
  const handleCopyClipboard = () => {
    const headers = ['Category', 'Description', 'Trade Size', 'Quantity', 'Unit', 'NEC Reference', 'Derivation', 'Locations'];
    const rows = bomItems.map((item) => [
      item.category,
      item.description,
      item.tradeSize,
      item.quantity,
      item.unit,
      item.necReference || 'NEC Standard',
      item.quantityDerivation || '',
      (item.locations || []).map((l) => `${l.quantity}x at ${l.location}`).join(', '),
    ]);

    const tsvContent = [headers.join('\t'), ...rows.map((r) => r.join('\t'))].join('\n');
    navigator.clipboard.writeText(tsvContent).then(() => {
      setCopiedToast(true);
      setTimeout(() => setCopiedToast(false), 2500);
    });
  };

  const toggleExpand = (id: string) => {
    setExpandedItemId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="material-manager-wrapper">
      {/* Top Header & Actions */}
      <div className="manager-header">
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Layers className="h-6 w-6 text-amber-500" />
            <span>Electrical Material List (BOM)</span>
          </h2>
          <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Accurate installation material quantities with NEC code compliance and location derivations.
          </p>
        </div>

        <div className="manager-actions-row">
          <button
            type="button"
            className="tool-btn"
            onClick={() => setShowConfigPanel(!showConfigPanel)}
            title="Configure NEC rules, waste margin, and enclosure entries"
          >
            <Settings className="h-4 w-4 text-amber-500" />
            <span>NEC Config</span>
          </button>

          <button
            type="button"
            className="tool-btn"
            onClick={recalculateBom}
            title="Re-run NEC quantity calculation from active wire schedule"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Recalculate</span>
          </button>

          <button
            type="button"
            className="tool-btn"
            onClick={handleCopyClipboard}
            title="Copy table to clipboard for Excel or Sheets"
          >
            {copiedToast ? <Check className="h-4 w-4 text-emerald-500" /> : <FileSpreadsheet className="h-4 w-4" />}
            <span>{copiedToast ? 'Copied!' : 'Copy TSV'}</span>
          </button>

          <button
            type="button"
            className="btn-action"
            onClick={handleExportExcel}
            style={{ fontWeight: 600 }}
          >
            <Download className="h-4 w-4" />
            <span>Export Excel (.xlsx)</span>
          </button>

          <button
            type="button"
            className="btn-primary"
            onClick={() => setShowAddModal(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Plus className="h-4 w-4" />
            <span>Add Item</span>
          </button>
        </div>
      </div>

      {/* KPI Cards Grid (Quantities & Material Metrics Only) */}
      <div className="kpi-cards-grid">
        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-title">Total Wire</span>
            <Zap className="h-4 w-4 text-amber-500" />
          </div>
          <div className="kpi-value">
            {Math.round(metrics.wireFt).toLocaleString()}{' '}
            <span className="kpi-unit">ft</span>
          </div>
          <span className="kpi-subtext">Phase, neutral &amp; EGC ground conductors</span>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-title">Total Conduit</span>
            <Disc className="h-4 w-4 text-blue-500" />
          </div>
          <div className="kpi-value">
            {Math.round(metrics.conduitFt).toLocaleString()}{' '}
            <span className="kpi-unit">ft</span>
          </div>
          <span className="kpi-subtext">{metrics.conduitSticks} sticks (10ft standard lengths)</span>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-title">Strut Straps</span>
            <Layers className="h-4 w-4 text-emerald-500" />
          </div>
          <div className="kpi-value">
            {metrics.strutStraps.toLocaleString()}{' '}
            <span className="kpi-unit">ea</span>
          </div>
          <span className="kpi-subtext">NEC 358.30 (within 3ft of boxes &amp; 10ft spans)</span>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-title">Couplings</span>
            <Link className="h-4 w-4 text-purple-500" />
          </div>
          <div className="kpi-value">
            {metrics.couplings.toLocaleString()}{' '}
            <span className="kpi-unit">ea</span>
          </div>
          <span className="kpi-subtext">10ft stick segment joint transitions</span>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-title">Enclosure Connectors</span>
            <Shield className="h-4 w-4 text-amber-500" />
          </div>
          <div className="kpi-value">
            {metrics.connectors.toLocaleString()}{' '}
            <span className="kpi-unit">ea</span>
          </div>
          <span className="kpi-subtext">Myers Hubs &amp; Raintight per NEC 314.15</span>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-title">Feeder Circuits</span>
            <Route className="h-4 w-4 text-indigo-400" />
          </div>
          <div className="kpi-value">
            {metrics.totalCircuits}{' '}
            <span className="kpi-unit">runs</span>
          </div>
          <span className="kpi-subtext">Distinct wire schedule raceways</span>
        </div>
      </div>

      {/* Collapsible NEC Configuration Panel */}
      {showConfigPanel && (
        <div className="nec-config-card">
          <div className="config-card-header">
            <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BookOpen className="h-4 w-4 text-amber-500" />
              <span>NEC Self-Generating Quantity Adder Parameters</span>
            </h4>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Controls how conduit supports, couplings, and enclosure hubs are calculated.
            </span>
          </div>

          <div className="config-grid">
            <div className="config-field">
              <label>Waste &amp; Pull Margin (%):</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="range"
                  min="0"
                  max="25"
                  step="1"
                  value={necConfig.wasteFactorPercent}
                  onChange={(e) =>
                    setNecConfig({ ...necConfig, wasteFactorPercent: Number(e.target.value) })
                  }
                  style={{ flex: 1 }}
                />
                <span style={{ fontWeight: 700, width: '40px' }}>{necConfig.wasteFactorPercent}%</span>
              </div>
            </div>

            <div className="config-field">
              <label>Conduit Stick Length:</label>
              <select
                value={necConfig.conduitStickLengthFt}
                onChange={(e) =>
                  setNecConfig({ ...necConfig, conduitStickLengthFt: Number(e.target.value) })
                }
                className="select-input"
              >
                <option value={10}>10 Feet (Standard EMT / RMC / PVC)</option>
                <option value={20}>20 Feet (Large Rigid / PVC sticks)</option>
              </select>
            </div>

            <div className="config-field">
              <label>Enclosure Hub Preference (NEC 314.15 / 312.2):</label>
              <select
                value={necConfig.enclosureEntryType}
                onChange={(e) =>
                  setNecConfig({
                    ...necConfig,
                    enclosureEntryType: e.target.value as any,
                  })
                }
                className="select-input"
              >
                <option value="AUTO">Auto (Top/Side: Myers Hubs, Bottom: Raintight)</option>
                <option value="TOP_SIDE">All Myers Watertight Sealing Hubs</option>
                <option value="BOTTOM">All Raintight Compression Connectors</option>
              </select>
            </div>

            <div className="config-field" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input
                type="checkbox"
                id="checkHardware"
                checked={necConfig.includeChannelHardware}
                onChange={(e) =>
                  setNecConfig({ ...necConfig, includeChannelHardware: e.target.checked })
                }
                style={{ width: '18px', height: '18px' }}
              />
              <label htmlFor="checkHardware" style={{ cursor: 'pointer', margin: 0 }}>
                Include 1-5/8&quot; Unistrut Channel &amp; Spring Nut Hardware
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="manager-toolbar-row">
        {/* Category Tabs */}
        <div className="category-tabs">
          {[
            { id: 'ALL', label: 'All Items' },
            { id: 'WIRE', label: 'Wire & Conductors' },
            { id: 'CONDUIT', label: 'Conduit' },
            { id: 'STRUT_STRAP', label: 'Strut Straps' },
            { id: 'COUPLING', label: 'Couplings' },
            { id: 'CONNECTOR', label: 'Connectors & Hubs' },
            { id: 'HARDWARE', label: 'Hardware' },
            { id: 'EQUIPMENT', label: 'Equipment' },
          ].map((cat) => (
            <button
              key={cat.id}
              type="button"
              className={`cat-tab-btn ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="search-box">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search items, locations, trade sizes, NEC..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Material List Items Table with Location Explanations */}
      <div className="material-table-container">
        <table className="material-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th style={{ width: '50px' }}>#</th>
              <th style={{ width: '130px' }}>Category</th>
              <th>Description &amp; Specification</th>
              <th style={{ width: '110px' }}>Trade Size</th>
              <th style={{ width: '120px' }}>Quantity</th>
              <th style={{ width: '80px' }}>Unit</th>
              <th style={{ width: '220px' }}>Installation Locations &amp; Derivation</th>
              <th style={{ width: '200px' }}>NEC Reference</th>
              <th style={{ width: '70px', textAlign: 'center' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length > 0 ? (
              filteredItems.map((item, idx) => {
                const isExpanded = expandedItemId === item.id;
                const locCount = item.locations?.length || 0;

                return (
                  <React.Fragment key={item.id}>
                    <tr className={item.isAutoGenerated ? 'row-auto' : 'row-manual'}>
                      <td style={{ textAlign: 'center', padding: '6px 4px' }}>
                        <button
                          type="button"
                          className="tool-btn"
                          style={{ padding: '4px', border: 'none', background: 'transparent' }}
                          title="Toggle location details & calculation derivation"
                          onClick={() => toggleExpand(item.id)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-amber-500" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-slate-400" />
                          )}
                        </button>
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{idx + 1}</td>
                      <td>
                        <span className={`cat-badge cat-${item.category.toLowerCase()}`}>
                          {item.category.replace('_', ' ')}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <input
                            type="text"
                            className="table-inline-input font-semibold"
                            value={item.description}
                            onChange={(e) => updateBomItem(item.id, { description: e.target.value })}
                          />
                          {item.specification && (
                            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                              {item.specification}
                            </span>
                          )}
                          {item.enclosureCompatibility && (
                            <span style={{ fontSize: '0.75rem', color: '#f59e0b', marginTop: '1px' }}>
                              {item.enclosureCompatibility}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="table-inline-input"
                          value={item.tradeSize}
                          onChange={(e) => updateBomItem(item.id, { tradeSize: e.target.value })}
                          style={{ width: '90px' }}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="table-inline-input text-right font-bold"
                          min="0"
                          step="1"
                          value={item.quantity}
                          onChange={(e) =>
                            updateBomItem(item.id, { quantity: Math.max(0, Number(e.target.value)) })
                          }
                          style={{ width: '85px' }}
                        />
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{item.unit}</td>
                      <td>
                        <button
                          type="button"
                          className="locations-summary-btn"
                          onClick={() => toggleExpand(item.id)}
                          title="Click to view full installation locations and quantity derivation"
                        >
                          <MapPin className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                          <span>
                            {locCount > 0
                              ? `${locCount} location${locCount > 1 ? 's' : ''}`
                              : 'General install'}
                          </span>
                        </button>
                      </td>
                      <td>
                        <div className="nec-tag-pill" title={item.notes || 'NEC Code Mandated'}>
                          <BookOpen className="h-3 w-3 text-amber-500" />
                          <span>{item.necReference || 'NEC Standard'}</span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="btn-icon-danger"
                          title="Remove item"
                          onClick={() => removeBomItem(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>

                    {/* Expandable Location Details & Derivation Card */}
                    {isExpanded && (
                      <tr className="expanded-details-row">
                        <td colSpan={10} style={{ padding: '0 16px 16px 16px', background: 'rgba(0, 0, 0, 0.03)' }}>
                          <div className="item-derivation-box">
                            {/* Why this quantity was obtained */}
                            <div className="derivation-section">
                              <div className="derivation-subhead">
                                <Info className="h-4 w-4 text-amber-500" />
                                <strong>Why this quantity was obtained:</strong>
                              </div>
                              <p className="derivation-text">
                                {item.quantityDerivation ||
                                  `Calculated from wire and conduit schedules in accordance with NEC code standards.`}
                              </p>
                            </div>

                            {/* Specific Installation Locations List */}
                            {item.locations && item.locations.length > 0 && (
                              <div className="locations-breakdown-section">
                                <div className="derivation-subhead">
                                  <MapPin className="h-4 w-4 text-blue-500" />
                                  <strong>
                                    Installation Locations Breakdown ({item.locations.length} points):
                                  </strong>
                                </div>

                                <div className="location-points-grid">
                                  {item.locations.map((loc, lIdx) => (
                                    <div key={lIdx} className="location-point-card">
                                      <div className="loc-point-header">
                                        <span className="loc-qty-pill">
                                          {loc.quantity} {item.unit}
                                        </span>
                                        <span className="loc-name-title">{loc.location}</span>
                                      </div>
                                      <div className="loc-purpose-desc">{loc.purpose}</div>
                                      {loc.runDescription && (
                                        <div className="loc-run-tag">
                                          <Route className="h-3 w-3 text-slate-400" />
                                          <span>{loc.runDescription}</span>
                                        </div>
                                      )}
                                      {loc.necCitation && (
                                        <div className="loc-nec-tag">
                                          <BookOpen className="h-3 w-3 text-emerald-500" />
                                          <span>{loc.necCitation}</span>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: '36px', color: 'var(--text-muted)' }}>
                  No items found matching the current category or filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Custom Item Modal */}
      {showAddModal && (
        <div className="equipment-modal-backdrop" onClick={() => setShowAddModal(false)}>
          <div
            className="equipment-modal-card"
            style={{ maxWidth: '520px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Plus className="h-5 w-5 text-amber-500" />
                <span>Add Material List Item</span>
              </h3>
              <button
                type="button"
                className="tool-button"
                onClick={() => setShowAddModal(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleAddItem} style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '16px' }}>
              <div className="form-group">
                <label>Category:</label>
                <select
                  value={newItemCategory}
                  onChange={(e) => setNewItemCategory(e.target.value as MaterialItemCategory)}
                  className="select-input"
                >
                  <option value="WIRE">Wire / Conductors</option>
                  <option value="CONDUIT">Conduit Raceway</option>
                  <option value="STRUT_STRAP">Strut Strap (Support)</option>
                  <option value="COUPLING">Coupling / Transition</option>
                  <option value="CONNECTOR">Connector / Hub</option>
                  <option value="HARDWARE">Unistrut Hardware</option>
                  <option value="EQUIPMENT">Equipment Item</option>
                  <option value="CUSTOM">Custom Material</option>
                </select>
              </div>

              <div className="form-group">
                <label>Item Description:</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. 2-1/2&quot; EMT Raintight Compression Connectors"
                  value={newItemDesc}
                  onChange={(e) => setNewItemDesc(e.target.value)}
                  className="text-input"
                />
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label>Trade Size:</label>
                  <input
                    type="text"
                    placeholder="e.g. 2-1/2&quot;, 4/0 AWG"
                    value={newItemSize}
                    onChange={(e) => setNewItemSize(e.target.value)}
                    className="text-input"
                  />
                </div>
                <div className="form-group">
                  <label>Unit:</label>
                  <select
                    value={newItemUnit}
                    onChange={(e) => setNewItemUnit(e.target.value)}
                    className="select-input"
                  >
                    <option value="ea">Each (ea)</option>
                    <option value="ft">Feet (ft)</option>
                    <option value="sticks">Sticks (10ft)</option>
                    <option value="sets">Sets / Pairs</option>
                    <option value="pkg">Package</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Quantity:</label>
                <input
                  type="number"
                  min="1"
                  required
                  value={newItemQty}
                  onChange={(e) => setNewItemQty(Number(e.target.value))}
                  className="text-input"
                />
              </div>

              <div className="form-group">
                <label>Installation Location:</label>
                <input
                  type="text"
                  placeholder="e.g. Inverter INV-1 bottom entry knockout"
                  value={newItemLocation}
                  onChange={(e) => setNewItemLocation(e.target.value)}
                  className="text-input"
                />
              </div>

              <div className="form-group">
                <label>Specific Purpose / Description:</label>
                <input
                  type="text"
                  placeholder="e.g. Raintight termination into NEMA 4X inverter cabinet"
                  value={newItemPurpose}
                  onChange={(e) => setNewItemPurpose(e.target.value)}
                  className="text-input"
                />
              </div>

              <div className="form-group">
                <label>NEC Code Reference:</label>
                <input
                  type="text"
                  placeholder="e.g. NEC 314.15, NEC 358.30"
                  value={newItemNec}
                  onChange={(e) => setNewItemNec(e.target.value)}
                  className="text-input"
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button
                  type="button"
                  className="tool-btn"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Add Item to Material List
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
